import { Fragment, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { endOfDay, isAfter } from 'date-fns';
import styled from 'styled-components';

import { useDebouncedValue } from '@components/component-helpers';
import {
  FilterAppliedProps,
  FilterComparer,
  FilterConfig,
  Filters,
  FilterValue,
  useCurrentFilter,
} from '@components/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconJob } from '@components/icons/job';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { abbreviateDate } from '@utils/date';

import { api } from 'api';
import { Loading } from 'components/Loading';
import { useStore } from 'models/Provider';
import type { JobState, JobTitleSearchEntry } from 'types/jobs';

import { FilterActionButtons } from './filter-action-buttons';
import { FilterComparerMenu } from './filter-comparer-menu';

const JOB_TITLE_COMPARERS: Array<FilterComparer> = ['is', 'not_is', 'any_of', 'none_of'];

function FilterApplicationJobTitleApplied({ data }: FilterAppliedProps) {
  const { t } = useStore();

  const ids = valueToJobIdArray(data.value);

  const jobsQuery = useQuery({
    queryKey: ['job-filter-ids', ids],
    queryFn: () => {
      return api.jobs.search({
        limit: 1000,
        queryData: {
          id: ids,
        },
      });
    },
    staleTime: 1000 * 60 * 60,
  });

  const value = jobsQuery.data?.results.map((job) => job.title).join(', ');

  return (
    <Filters.AppliedFilter
      icon={<IconJob />}
      category={t('applicants.filters.job.title')}
      value={value || t('filters.loading')}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

const valueToJobIdArray = (value?: FilterValue) =>
  value ? (Array.isArray(value) ? value : [String(value)]) : [];

function FilterApplicationJobTitleMenu() {
  const { t } = useStore();
  const { state, actions, hasValue } = useCurrentFilter();
  const [_search, setSearch] = useState('');

  const search = useDebouncedValue(_search, 350);

  const jobsQuery = useQuery({
    queryKey: ['job-title-search', search],
    queryFn: () => api.jobs.search({ title: search }),
    enabled: Boolean(state.comparer),
    keepPreviousData: true,
  });

  const ids = valueToJobIdArray(state.value);

  const selectedJobsQuery = useQuery({
    queryKey: ['job-filter-selected-ids', ids],
    queryFn: () => api.jobs.search({ limit: 1000, queryData: { id: ids } }),
    enabled: ids.length > 0,
    keepPreviousData: true,
  });

  const selectedJobsNotInResult =
    ids.length > 0
      ? (selectedJobsQuery.data?.results || []).filter((selectedJob) => {
          return !Boolean(jobsQuery.data?.results.find((job) => job.id === selectedJob.id));
        })
      : [];

  const jobList = [...selectedJobsNotInResult, ...(jobsQuery.data?.results || [])];

  const count = jobsQuery.data?.count || 0;

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.job.title')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      {state.comparer && (
        <Filters.ListItem
          heading
          label={t('comparers.' + state.comparer).replace('...', '')}
          icon={<IconArrowLeft />}
          onClick={actions.unselectComparer}
        />
      )}

      <Filters.ListSeparator />

      {!state.comparer && (
        <FilterComparerMenu
          comparers={JOB_TITLE_COMPARERS}
          onComparerSelect={actions.selectComparer}
        />
      )}

      {state.comparer && (
        <Fragment>
          <Stack space={4}>
            <SearchBox onChange={setSearch} autoComplete="off" />
            {jobsQuery.data && (
              <div>
                {count > 8 && (
                  <SearchResultHeading>
                    <Text
                      font="bold14"
                      color="purple100"
                      caps
                      text={t('applicants.filters.job.showing')}
                    />
                  </SearchResultHeading>
                )}
                <Fragment>
                  {jobsQuery.isLoading && <Loading />}
                  {jobList.map((job) => (
                    <ValueRow key={job.id} job={job} />
                  ))}
                </Fragment>
              </div>
            )}
          </Stack>
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}
    </Filters.List>
  );
}

const SearchResultHeading = styled.div({
  height: '2.5rem',
  display: 'flex',
  alignItems: 'center',
});

const ValueRow = ({ job }: { job: JobTitleSearchEntry }) => {
  const { t, lang } = useStore();

  const { state, actions, isMultiComparer } = useCurrentFilter();

  const value = String(job.id);
  const label = job.title;

  let isSelected = false;

  if (state.value !== undefined) {
    isSelected = isMultiComparer
      ? (state.value as Array<string>).includes(value)
      : state.value === value;
  }

  return (
    <Filters.ListItem
      key={job.id}
      icon={<Filters.SelectionIcon isSelected={isSelected} />}
      onClick={() => actions.selectValue(value)}
      label={
        <Fragment>
          <Text
            style={{ whiteSpace: 'normal' }}
            font="bold14"
            text={label || t('applicants.filters.job.notitle')}
          />
          <Text
            font="regular14"
            color="purple80"
            text={`${t('applicants.filters.job.state', t('jobs.tabs.' + jobStateToStatus(job)))}`}
          />
          <Text
            font="regular14"
            color="purple80"
            text={t('job.datecreated', abbreviateDate(job.dateCreated, lang))}
          />
        </Fragment>
      }
    />
  );
};

type ComputeJobStatusProps = {
  isPublished: boolean;
  isArchived: boolean;
  deadline: string | null;
};

const isJobExpired = (job: ComputeJobStatusProps) => {
  if (!job.deadline) return false;

  const today = new Date();
  const endOfDeadlineDay = endOfDay(new Date(job.deadline));
  return isAfter(today, endOfDeadlineDay);
};

const jobStateToStatus = (job: ComputeJobStatusProps): JobState => {
  if (job.isArchived) return 'archived';
  if (isJobExpired(job)) return 'expired';
  if (job.isPublished) return 'published';
  return 'drafts';
};

export function useFilterApplicationJobTitle(): FilterConfig {
  const { t } = useStore();

  return useMemo(() => {
    return {
      category: 'job',
      label: t('applicants.filters.job.title'),
      icon: <IconJob />,
      AppliedFilter: FilterApplicationJobTitleApplied,
      Menu: FilterApplicationJobTitleMenu,
      toQueryData: (filter) => {
        switch (filter.comparer) {
          case 'is':
          case 'any_of':
            return { job: filter.value };
          case 'not_is':
          case 'none_of':
            return { job__exclude: filter.value };
          default:
            return {};
        }
      },
    };
  }, [t]);
}
